import axios from 'axios';
import { getMimicUser } from './mimic';
import { ACCESS_TOKEN_NAME, ACCESS_TOKEN_USER, REFRESH_TOKEN_NAME } from '../const';
import {
  getActiveToken,
  getActiveTokenSlot,
  getStoredRefreshToken,
} from './jwt';

const host = process.env.REACT_APP_BACKEND_URL;

const axiosInstance = axios.create({
  baseURL: host,
});

// Pick the most useful text the backend gave us. Most endpoints answer with
// `{ message }`, but the access guards (e.g. the unpaid-invoice leads block)
// answer with `{ error }`.
const extractMessage = (response) =>
  response.data?.message ||
  response.data?.error ||
  response.statusText ||
  `Request failed with status ${response.status}`;

// The backend's isAuth middleware answers with 400 "Invalid token" when the
// JWT fails verification (expired, tampered, rotated). That is the exact and
// unambiguous "your session is gone" signal — role / permission denials use
// 401 / 403 and must NOT force logout.
const isSessionLost = (response) =>
  response &&
  response.status === 400 &&
  response.data &&
  response.data.message === 'Invalid token';

// Guard against multiple concurrent 401-shaped responses each trying to
// redirect. First one wins; the rest just reject.
let sessionRedirectInFlight = false;
const forceSignOutAndRedirect = () => {
  if (sessionRedirectInFlight) return;
  sessionRedirectInFlight = true;
  try {
    window.localStorage.removeItem(ACCESS_TOKEN_NAME);
    window.localStorage.removeItem(ACCESS_TOKEN_USER);
    window.localStorage.removeItem(REFRESH_TOKEN_NAME);
  } catch (_) {
    /* localStorage unavailable in some contexts */
  }
  if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/auth/')) {
    window.location.assign('/auth/sign-in');
  }
};

const writeSlot = (name, value) => {
  try {
    if (value) {
      window.localStorage.setItem(name, value);
    } else {
      window.localStorage.removeItem(name);
    }
  } catch (_) {
    /* localStorage unavailable */
  }
};

// Serialize refresh calls: many components fire requests in parallel, and a
// burst of 400s must not each POST to /refresh-token. First 400 kicks off the
// refresh; the rest wait on the same promise. Whether they retry depends on
// whether the refresh succeeds.
let refreshInFlight = null;
const runRefresh = () => {
  if (refreshInFlight) return refreshInFlight;
  const refreshToken = getStoredRefreshToken();
  if (!refreshToken) {
    return Promise.resolve(null);
  }
  refreshInFlight = axiosInstance
    .post(
      '/api/my-pages/auth/refresh-token',
      { refreshToken },
      { skipAuthRefresh: true, skipMimic: true, headers: { Authorization: '' } },
    )
    .then((res) => {
      const nextAccess = res?.data?.token;
      const nextRefresh = res?.data?.refreshToken;
      if (!nextAccess) return null;
      writeSlot(ACCESS_TOKEN_NAME, nextAccess);
      if (nextRefresh) writeSlot(REFRESH_TOKEN_NAME, nextRefresh);
      return nextAccess;
    })
    .catch(() => null)
    .finally(() => {
      refreshInFlight = null;
    });
  return refreshInFlight;
};

axiosInstance.interceptors.request.use((config) => {
  // Attach the current auth token on every outgoing request by reading it
  // straight from localStorage. Previously the token was pinned once onto
  // `axios.defaults.headers.common.Authorization` at login/boot, which meant
  // clearing localStorage (in another tab, or in DevTools) did NOT stop the
  // instance from sending the pinned Bearer — subsequent requests kept
  // succeeding. Reading per-request makes localStorage the single source of
  // truth so a real signout in any tab immediately stops working here.
  // Callers passing an explicit Authorization header (e.g. usersApi's
  // password-reset flows) are left untouched.
  const alreadyHasAuth = config.headers && config.headers.Authorization;
  if (!alreadyHasAuth) {
    const accessToken = getActiveToken();
    if (accessToken) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${accessToken}`;
    } else if (config.headers && config.headers.Authorization) {
      delete config.headers.Authorization;
    }
  }

  if (config.skipMimic) return config;
  const mimic = getMimicUser();
  if (mimic && mimic.id && mimic.type) {
    config.headers = config.headers || {};
    config.headers['X-Mimic-User-Id'] = mimic.id;
    config.headers['X-Mimic-User-Type'] = mimic.type;
  }
  return config;
});

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const { response, config } = error;

    if (response && response.status === 403) {
      console.warn('Permission denied:', response.data?.message || 'Access denied');
    }

    if (!response) {
      return Promise.reject(error);
    }

    // Silent refresh: the backend hands out a 30-day refreshToken alongside
    // the 12h access token. If a request fails with the exact "Invalid token"
    // signal and we've got a refreshToken in storage, try to swap in a fresh
    // access token once and replay the original request. Only the admin/user
    // NAME slot is eligible — kundvy (impersonation) tokens are single-shot
    // and have no paired refreshToken.
    if (
      isSessionLost(response)
      && config
      && !config.skipAuthRefresh
      && !config.isAuthRetry
      && getActiveTokenSlot() === ACCESS_TOKEN_NAME
      && getStoredRefreshToken()
    ) {
      const nextAccess = await runRefresh();
      if (nextAccess) {
        const retryConfig = {
          ...config,
          isAuthRetry: true,
          headers: {
            ...(config.headers || {}),
            Authorization: `Bearer ${nextAccess}`,
          },
        };
        return axiosInstance(retryConfig);
      }
    }

    if (isSessionLost(response)) {
      forceSignOutAndRedirect();
    }

    // This interceptor used to reject with the raw AxiosResponse. A response
    // object has no `message`, so every caller doing `err.message` (the auth
    // screens among them) fell back to a generic "Something went wrong" and the
    // real reason — "Customer is inactive", "No such email" — never reached the
    // user. Reject with a real Error carrying the backend message, while
    // keeping `data` / `status` / `response` reachable so the many callers
    // written against the old shape keep working.
    const normalizedError = new Error(extractMessage(response));
    normalizedError.data = response.data;
    normalizedError.status = response.status;
    normalizedError.statusText = response.statusText;
    normalizedError.headers = response.headers;
    normalizedError.config = response.config;
    normalizedError.response = response;

    return Promise.reject(normalizedError);
  },
);

export default axiosInstance;
